import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withStyles } from '@material-ui/core';
import { autorun } from 'mobx';
import ValidationReason from '../../ValidationReason';
import { getShipmentsData } from '../../../utils';
import { useDynamicRoutingValidationContext } from '../../../context/DynamicRoutingValidationContext';
import { UNAPPOINTED_PROS_CONTENT } from './constants';
import { SELECTION_MODE } from '../constants';
import CustomGrid from '../CustomGrid';
import GridConfig from './gridConfig';
import { constructSelectedShipmentsStatusBar } from '../unappointedProsUtils';

const styles = (theme: { disableButtonText: string }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%'
  },
  messageText: {
    color: theme.disableButtonText,
    fontWeight: 500,
    marginBottom: 5
  }
});

type ShipmentRow = { proId: string };

type UnappointedProsProps = {
  classes: Record<string, string>;
  data: Array<{ proId: string }>;
  IBPlanningStore: { shipmentsList: ShipmentRow[] };
  currentStepMessage: string;
};

const UnappointedPros = ({ classes, data, IBPlanningStore, currentStepMessage }: UnappointedProsProps) => {
  const [shipments, setShipments] = useState<ShipmentRow[]>([]);
  const { initializeProsForCityRoute } = useDynamicRoutingValidationContext();
  const initializedProIdsRef = useRef('');

  const populateShipmentsData = useCallback(() => {
    if (data) {
      const shipmentsData = getShipmentsData({
        proIdList: data,
        shipmentsList: IBPlanningStore.shipmentsList
      });
      setShipments(shipmentsData);
    }
  }, [data, IBPlanningStore.shipmentsList]);

  useEffect(() => {
    const dispose = autorun(populateShipmentsData);
    return () => dispose();
  }, [data, populateShipmentsData]);

  useEffect(() => {
    if (!data?.length) {
      return;
    }
    const proIdsKey = data
      .map((item) => item.proId)
      .sort()
      .join(',');
    if (initializedProIdsRef.current === proIdsKey) {
      return;
    }
    initializedProIdsRef.current = proIdsKey;
    initializeProsForCityRoute([]);
  }, [data, initializeProsForCityRoute]);

  return (
    <div className={classes.container}>
      <ValidationReason message={currentStepMessage} />
      <p className={classes.messageText}>{UNAPPOINTED_PROS_CONTENT.helperText}</p>
      <CustomGrid
        title={UNAPPOINTED_PROS_CONTENT.gridTitle}
        GridConfig={GridConfig}
        data={shipments}
        showSearchBar
        selectionMode={SELECTION_MODE.INCLUDED_FOR_CITY_ROUTE}
        constructStatusBar={constructSelectedShipmentsStatusBar}
      />
    </div>
  );
};

export default withStyles(styles)(UnappointedPros);
